import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import uuidv1 from 'uuid/v1';
import { connect } from 'react-redux';
import { Autocomplete } from '@mic3/platform-ui';

import { bind, debounce, memoize } from 'app/utils/decorators/decoratorUtils';
import { loadRelationDefinitionTypeahead } from 'store/actions/entities/relationshipsActions';
import { TypeaheadChipInitials } from 'app/components/organisms/Form/Typeahead/abstract/TypeaheadChip';
import { isEmpty, shallowEquals } from 'app/utils/utils';
import { commonElementExists, findIntersection } from 'app/utils/array/array-utils';
import { getClassAncestors } from 'app/utils/classification/classificationUtils';

const getFields = (field, typesArray) => {
    return typesArray.map(uri => ({ field, op: '=', value: uri }));
};

export const reverseDef = def => ({
    ...def,
    description: def.relatedDescription,
    relatedDescription: def.description,
    type: def.relatedType,
    relatedType: def.type,
    reverse: true,
});
/**
 * Select one or more groups using lazy loading.
 */
class RelationDefinitionTypeahead extends PureComponent {
    static propTypes = {
        ...Autocomplete.propTypes,
        value: PropTypes.any,
        loadOptions: PropTypes.func.isRequired,
        extraQueryFields: PropTypes.array,
        fromType: PropTypes.string.isRequired,
        toType: PropTypes.string.isRequired,
        filterBy: PropTypes.arrayOf(PropTypes.Object),
        excludeBy: PropTypes.arrayOf(PropTypes.Object),
        orderBy: PropTypes.arrayOf(PropTypes.Object),
        isLoading: PropTypes.bool,
        options: PropTypes.arrayOf(PropTypes.object),
    };

    state = { key: uuidv1(), isLoading: false, options: [], lastRequest: 0, toUris: [], fromUris: [] };


    componentDidUpdate(prevProps) {
        if (!shallowEquals(this.props, prevProps, ['fromType', 'toType', 'filterBy', 'excludeBy', 'orderBy'])) {
            this.setState({ isLoading: false, options: [], lastRequest: 0, toUris: [], fromUris: [], key: uuidv1() });
        }
    }

    @bind
    @memoize()
    getAncestorsUris(fromType, toType, entityTypes) {
        let fromUris = [];
        let toUris = [];
        if (!entityTypes?.length) return { fromUris, toUris };
        const getUris = (type) => {
            let uris = [type];
            const match = entityTypes.find(({ uri }) => uri === type);
            if (match?.id) {
                const ancestors = getClassAncestors(match.id, entityTypes);
                if (ancestors?.length) {
                    uris = ancestors.map(({ uri }) => uri);
                    if (!uris.includes(type)) {
                        uris.unshift(type);
                    }
                }
            }
            return uris;
        }

        try {
            if(fromType){
                fromUris = getUris(fromType);
            }
            if(toType){
                toUris = getUris(toType)
            }
        } catch (error) {

        }
        return { fromUris, toUris };
    }


    @bind
    getRelationDefinitions(data, toUris, fromUris) {
        if (isEmpty(data)) {
            return [];
        }
        if (!commonElementExists(toUris, fromUris)) {
            return data.map(rel => {
                const reverse = fromUris.includes(rel?.relatedType) && toUris.includes(rel?.type);
                return reverse ? reverseDef(rel) : rel;
            });
        }
        const commonParents = findIntersection(toUris, fromUris);
        return data
            .map(rel => {
                if (commonParents?.includes(rel?.type) && commonParents?.includes(rel?.relatedType)) {
                    return [rel, reverseDef(rel)];
                }
                const reverse = fromUris.includes(rel?.relatedType) && toUris.includes(rel?.type);
                return reverse ? reverseDef(rel) : rel;
            })
            .flat();
    }

    @bind
    @debounce()
    suggest(event) {
        const { fromType, toType, entityTypes } = this.props;
        
        this.setState({ isLoading: true }, async () => {
            const { value, loadOptions, filterBy, excludeBy, orderBy, extraQueryFields } = this.props;
            const filters = filterBy ? [...filterBy] : [];
            const { toUris = [], fromUris = [] } = this.getAncestorsUris(fromType, toType, entityTypes);
       
            if (fromUris.length && toUris.length) {
                filters.push({
                    or: [
                        [{ or: getFields('type', fromUris) }, { or: getFields('relatedType', toUris) }],
                        [{ or: getFields('type', toUris) }, { or: getFields('relatedType', fromUris) }],
                    ],
                })
            } else if (fromUris.length) {
                filters.push({
                    or: [
                        { field: 'type', op: 'in', value: fromUris },
                        { field: 'relatedType', op: 'in', value: fromUris },
                    ],
                });
            } else if (toUris.length) {
                filters.push({
                    or: [
                        { field: 'type', op: 'in', value: toUris },
                        { field: 'relatedType', op: 'in', value: toUris },
                    ],
                });
            }

            const query = event.target.value;
            
            if (query) {
                filters.push({
                    or: [
                        { field: 'id', op: '=', value: query, cast: 'text' },
                        { field: 'description', op: 'contains', value: query },
                        { field: 'relatedDescription', op: 'contains', value: query },
                    ],
                });
            }
            if (value && !isEmpty(value) ) {
                const idsToExclude = ((Array.isArray(value) && value.length > 0) ? value : [value]).map(({ id }) => id);
                filters.push({ field: 'id', op: 'not in', value: idsToExclude });
            }
            
            const data = await loadOptions(
                {
                    filterBy: filters,
                    excludeBy: excludeBy,
                    orderBy: orderBy,
                },
                extraQueryFields
            );
            
            const normalizedOptions = this.getRelationDefinitions(data, toUris, fromUris);
            this.setState({
                isLoading: false,
                options: normalizedOptions,
                toUris,
                fromUris,
            });
        });
    }

    @bind
    optionTemplate(data) {
        const { id, description } = data;
        const label = `${description || 'Description not available'} (${id ? id.slice(0, 8) : 'Id not available'})`;
        const title = description;
        return {
            ChipProps: {
                avatar: <TypeaheadChipInitials initials={description} />,
            },
            option: <span title={title}>{label}</span>,
            label,
        };
    }

    render() {
        const { loadOptions, fromType, toType, filterBy, excludeBy, orderBy, ...typeaheadProps } = this.props; // eslint-disable-line no-unused-vars
        const { isLoading, options, key } = this.state;
        return (
            <Autocomplete key={key} {...typeaheadProps} optionTemplate={this.optionTemplate} suggest={this.suggest} options={options} isLoading={isLoading} />
        );
    }
}

export default connect(
    (state) => ({
        entityTypes: state.app.allPrimaryClasses.records || [],
    }),
    { loadOptions: loadRelationDefinitionTypeahead }
)(RelationDefinitionTypeahead);
